import type { CellEditIn, RowDeletionIn, RowInsertionIn } from './generated'
import type {
  CellEdit,
  WorkbookRowDeletion,
  WorkbookRowInsertion,
} from '@/components/customer/workbookGrid'

interface CancellableRequest<T> extends Promise<T> {
  cancel(): void
}

export async function resolveWorkbookRequest<T>(
  request: CancellableRequest<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted) request.cancel()
  const abort = () => request.cancel()
  signal?.addEventListener('abort', abort, { once: true })
  try {
    return await request
  } finally {
    signal?.removeEventListener('abort', abort)
  }
}

export function requireWorkbookBlob(value: unknown): Blob {
  if (value instanceof Blob) return value
  throw new Error('The workbook response was not a downloadable file. Reload and try again.')
}

export function toApiCellEdits(edits: CellEdit[]): CellEditIn[] {
  return edits.map((edit) => ({
    row: edit.row,
    col: edit.col,
    value_type: edit.value_type,
    value: edit.value instanceof Date ? edit.value.toISOString() : edit.value,
  }))
}

export function toApiRowInsertions(insertions: WorkbookRowInsertion[]): RowInsertionIn[] {
  return insertions.map((insertion) => ({
    id: insertion.id,
    row: insertion.row,
    template_row: insertion.template_row,
    template_source_row: insertion.template_source_row,
    cells: insertion.cells.map((cell) => ({
      col: cell.col,
      value_type: cell.value_type,
      value: cell.value instanceof Date ? cell.value.toISOString() : cell.value,
    })),
  }))
}

export function toApiRowDeletions(deletions: WorkbookRowDeletion[]): RowDeletionIn[] {
  return deletions.map((deletion) => ({ row: deletion.row }))
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}
